import {AxiosError, AxiosResponse} from "axios";
import {CopyToClipboard} from "components/src/common/CopyToClipboardButton";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useRenderOnce} from "components/src/hook/UseRenderOnce";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import DOMPurify from "dompurify";
import {Dispatch, SetStateAction, useRef, useState} from "react";
import Button from "react-bootstrap/Button";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import {useNavigate} from "react-router-dom";

/**
 * The owner should handle the OnClose event. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause: DialogCloseCause) => void;

interface TfaSetupWizardProps {
    is2FAEnabledFunc : () => Promise<AxiosResponse<boolean>>
    generateSecretFunc : () => Promise<AxiosResponse<string>>
    createSecretQRCodeFunc : (secret : string) => Promise<AxiosResponse<string>>
    set2FAEnabledFunc : (enabled : boolean) => Promise<AxiosResponse<void>>
    validateOTPFunc : (otpCode : string, secret : string) => Promise<AxiosResponse<void>>
    setSecretFunc : (secret : string) => Promise<AxiosResponse<void>>
    onClose: OnClose
    /** When true the disable page is skipped and disabling 2FA is not possible */
    tfaRequired?: boolean
}

interface WizardPage {
    id: "disable" | "intro" | "download" | "qrcode" | "validate" | "finish"
    title: string
}

interface DisableProps {
    disable: boolean
    setDisable: Dispatch<SetStateAction<boolean>>
    tfaRequired: boolean
}

const sanitizeHTML = (html : string) => {
    return {__html: DOMPurify.sanitize(html, { ADD_ATTR: ['target'] })};
}

/**
 * Disable wizard page component
 */
const Disable = ({disable, setDisable, tfaRequired} : DisableProps) => {
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    return (
        <div>
            <div dangerouslySetInnerHTML={sanitizeHTML(t(["2fa-setup-wizard.disable-text"]))} />

            <Form.Group>
                <Form.Check type="radio"
                            id="disable-2fa"
                            checked={disable}
                            onChange={() => setDisable(true)}
                            label={t('2fa-setup-wizard.disable')}
                            disabled={tfaRequired}
                />
                <Form.Check type="radio"
                            id="keep-2fa-enabled"
                            checked={!disable}
                            onChange={() => setDisable(false)}
                            label={t('2fa-setup-wizard.keep-enabled')}
                />
            </Form.Group>
        </div>
    )
}

/**
 * Intro wizard page component
 */
const Intro = () => {
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    return (
        <div dangerouslySetInnerHTML={sanitizeHTML(t(["2fa-setup-wizard.intro"]))} />
    )
}

/**
 * Download wizard page component
 */
const Download = () => {
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    return (
        <div dangerouslySetInnerHTML={sanitizeHTML(t(["2fa-setup-wizard.download"]))} />
    )
}

interface QRCodeProps {
    qrCode: string | undefined
    secretKey: string | undefined
}

/**
 * QR-Code wizard page component
 */
const QRCode = ({qrCode, secretKey}: QRCodeProps) =>
{
    const {t} = useTranslationWithOverride();

    return (
        <div>
            <div dangerouslySetInnerHTML={sanitizeHTML(t(["2fa-setup-wizard.scan-qr-code"]))} />

            <img src={`data:image/png;base64,${qrCode}`} alt="QR-code"/>

            <div className="d-flex flex-row">
                <FloatingLabel label={t('2fa-setup-wizard.secret-key-label')}
                               className="text-secondary w-100">
                    <Form.Control type="text" value={secretKey} placeholder="SecretKey" disabled={true}
                                  readOnly={true}/>
                </FloatingLabel>
                <CopyToClipboard value={secretKey}/>
            </div>
        </div>
    )
}

interface ValidateProps {
    otpCode: string
    setOtpCode: Dispatch<SetStateAction<string>>
}

/**
 * Validate wizard page component
 */
const Validate = ({otpCode, setOtpCode}: ValidateProps) => {
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    return (
        <div>
            <div dangerouslySetInnerHTML={sanitizeHTML(t(["2fa-setup-wizard.validate"]))} />

            <FloatingLabel label={t('2fa-setup-wizard.validate-label')} className="text-secondary mt-3">
                <Form.Control type="text"
                              value={otpCode}
                              placeholder="Enter the code generated by the Authenticator App"
                              onChange={e => setOtpCode(e.target.value)}/>
            </FloatingLabel>
        </div>
    )
}

const Finish = () => {
    const {t} = useTranslationWithOverride();

    return (
        <div dangerouslySetInnerHTML={sanitizeHTML(t(["2fa-setup-wizard.finish"]))} />
    )
}

export const TfaSetupWizard = ({
                                   is2FAEnabledFunc,
                                   generateSecretFunc,
                                   createSecretQRCodeFunc,
                                   set2FAEnabledFunc,
                                   validateOTPFunc,
                                   setSecretFunc,
                                   onClose,
                                   tfaRequired = false}: TfaSetupWizardProps) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The current wizard index
     */
    const [wizardIndex, setWizardIndex] = useState<number>(0);
    /**
     * True if TFA is enabled
     */
    const [isTFAEnabled, setIsTFAEnabled] = useState<boolean>(false);
    /**
     * True if TFA should be disabled
     */
    const [disable, setDisable] = useState<boolean>(false);
    /**
     * The base64 encoded QR-code containing the 2FA secret
     */
    const [secretKeyQRCode, setSecretKeyQRCode] = useState<string>("");
    /**
     * The 2FA secret
     */
    const [secretKey, setSecretKey] = useState<string>("");
    /**
     * The TOTP code provided by the user
     */
    const [otpCode, setOtpCode] = useState<string>("");
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * Reference to the element which should receive focus on show
     */
    const focusRef = useRef<HTMLButtonElement>(null);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    /**
     * The Wizard pages
     */
    const [wizardPages] = useState<WizardPage[]>(
        [
            {id: "disable", title: t('2fa-setup-wizard.disable-title')},
            {id: "intro", title: t('2fa-setup-wizard.intro-title')},
            {id: "download", title: t('2fa-setup-wizard.download-title')},
            {id: "qrcode", title: t('2fa-setup-wizard.scan-qr-code-title')},
            {id: "validate", title: t('2fa-setup-wizard.validate-title')},
            {id: "finish", title: t('2fa-setup-wizard.finish-title')},
        ]
    );

    /**
     * Check if TFA is enabled
     */
    useRenderOnce(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        is2FAEnabledFunc().then(response =>
        {
            setIsTFAEnabled(response.data);

            if (!response.data) {
                setWizardIndex(1);
            }
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error checking TFA enabled status", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    });

    useRenderOnce(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        generateSecretFunc().then((response) =>
        {
            setSecretKey(response.data);

            createSecretQRCodeFunc(response.data).then(response => {
                setSecretKeyQRCode(response.data);
            })
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error initializing TFA ", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    });

    // called when the dialog is closed without clicking download
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    const currentPage = () => {
        return wizardPages[wizardIndex];
    }

    const isLastPage = () => {
        return wizardIndex === (wizardPages.length - 1)
    }

    const isFirstPage = () => {
        return wizardIndex === 0
    }

    const nextPage = () => {
        if (isLastPage()) {
            throw new Error("Last page reached")
        }

        setWizardIndex(index => index + 1)
    }

    const previousPage = () => {
        if (isFirstPage()) {
            throw new Error("At first page")
        }

        setErrorMessage(undefined);

        setWizardIndex(index => index - 1)
    }

    const cancel = () => {
        onClose(DialogCloseCause.Cancel);
    }

    const saveTFASecret = (enableTFA : boolean) =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        setSecretFunc(secretKey).then(() => {
            set2FAEnabledFunc(enableTFA).then(() => {
                onClose(DialogCloseCause.Succes);
            })
                .catch((err: Error | AxiosError) =>
                {
                    logger.error("Error enabling 2FA", err);
                    Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                    setErrorMessage(Utils.getErrorMessage(err, t));
                })
                .finally(() => {
                    if (setSpinnerStateCounter !== undefined) {
                        setSpinnerStateCounter(count => count - 1);
                    }
                })
        })
        .catch((err: Error | AxiosError) =>
        {
            logger.error("Error enabling 2FA", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }

    const finish = () => {
        saveTFASecret(!disable);
    }

    const isNextDisabled = () => {
        return (isLastPage() || (currentPage().id === "validate" && StringUtils.isBlank(otpCode) || disable));
    }

    const isPreviousDisabled = () => {
        return isFirstPage() || (currentPage().id === "intro" && !isTFAEnabled);
    }

    const isFinishDisabled = () => {
        return !(isLastPage() || (currentPage().id === "disable" && disable));
    }

    const validateOTPCode = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        validateOTPFunc(otpCode, secretKey).then(() => {})
            .then(() => {
                nextPage();
            })
            .catch((err: Error | AxiosError) =>
            {
                logger.error("Error validating OTP Code", err);

                if (err instanceof AxiosError && err.response?.status === 401) {
                    setErrorMessage(t('2fa-setup-wizard.validate-incorrect-otp-code'))
                }
                else {
                    setErrorMessage(Utils.getErrorMessage(err, t));
                }
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }

    const onClickNext = () =>
    {
        setErrorMessage(undefined);

        if (currentPage().id === "validate")
        {
            validateOTPCode();
            // if validation succeeds, the next page will be activated by validateOTPCode
            return;
        }

        nextPage();
    }

    return (
        <Modal show={true}
               onHide={() => onHideDialog()}
               size="xl"
               fullscreen="lg-down"
               scrollable={false}
               backdrop="static"
               onEntered={() => {focusRef.current?.focus()}}
        >
            <Modal.Header closeButton>
                <Modal.Title>{currentPage().title}</Modal.Title>
            </Modal.Header>
            <div className="row flex-grow-1">
                <main className="col d-flex flex-column overflow-auto vh-80">
                    <Modal.Body>
                        <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                     tooltipPlacement={"bottom"}/>
                        {currentPage().id === "disable" && <Disable disable={disable} setDisable={setDisable} tfaRequired={tfaRequired}/>}
                        {currentPage().id === "intro" && <Intro/>}
                        {currentPage().id === "download" && <Download/>}
                        {currentPage().id === "qrcode" && <QRCode qrCode={secretKeyQRCode} secretKey={secretKey}/>}
                        {currentPage().id === "validate" && <Validate otpCode={otpCode} setOtpCode={setOtpCode}/>}
                        {currentPage().id === "finish" && <Finish/>}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" disabled={isPreviousDisabled()} onClick={previousPage}>
                            {t('wizard.previous-button')}
                        </Button>
                        <Button variant="primary" disabled={isNextDisabled()} onClick={onClickNext} ref={focusRef}>
                            {t('wizard.next-button')}
                        </Button>
                        <Button variant="danger" onClick={cancel}>
                            {t('wizard.cancel-button')}
                        </Button>
                        <Button variant="success" disabled={isFinishDisabled()} onClick={finish}>
                            {t('wizard.finish-button')}
                        </Button>
                    </Modal.Footer>
                </main>
            </div>
        </Modal>
    )
}
